import { AppError } from '@vk-hr-tek/core/error';

import {
  UserPermissions,
  UserResponse as User,
  CompanyEventTypeItem,
  DateRange,
  CertificateError,
} from '@app/gen/users';
import { ManagerSubstituteDeleteOption, Unit } from '@app/gen/settings';
import { CandidateItem } from '@app/gen/candidates';
import { UserProfileEmployee } from '@app/gen/user';

export type ExtendedUser = Omit<User, 'certificate'> & {
  certificate: Omit<User['certificate'], 'status' | 'smev_errors'> & {
    status:
      | 'waiting_to_create'
      | 'need_verify'
      | 'waiting_confirmation'
      | 'verifying'
      | 'released'
      | 'expired'
      | 'error'
      | 'need_verify_code'
      | 'release_paused';
    errors?: Array<
      Omit<NonNullable<User['certificate']['errors']>[number], 'code'> & {
        code: SmevErrorCode;
        source?: CertificateError['source'];
      }
    >;
  };
};

export type SmevErrorCode =
  | 'passportExpired'
  | 'passportReplaced'
  | 'passportIssuedWithViolation'
  | 'passportIsSearched'
  | 'passportDestroyed'
  | 'passportOwnerDied'
  | 'passportDefect'
  | 'passportLost'
  | 'passportCitizenshipStopped'
  | 'passportNotValid'
  | 'snilsNotMatchWithPfr'
  | 'innNotMatchWithFns'
  | 'notValidSnilsChecksum'
  | 'pfrNotAvailable'
  | 'fnsNotAvailable'
  | 'fmsNotAvailable'
  | 'organizationNotFoundInFns'
  | 'unknown'
  | 'unknownConfirmationError'
  | 'userNotFoundInEsia'
  | 'confirmationRejected'
  | 'confirmationTimeout';

interface Employee {
  id: string;
  name: string;
  personnelNumber: string;
  email: string | undefined;
  position: string | undefined;
  phone: string | undefined;
  company: {
    id: string;
    name: string;
  };
}

interface EventType {
  substitutes: Employee[];
  eventType: {
    id: string;
    name: string;
  };
}

interface SubstituteEventTypeGroup {
  eventType: {
    id: string;
    name: string;
  };
  substitutes: {
    id: string;
    name: string;
    personalNumber: string;
    position?: string;
    company: string;
    unit_name?: string;
    dates?: DateRange[];
    availableOptions?: ManagerSubstituteDeleteOption[];
  }[];
}

export interface CompanyPositionSubstitutes {
  company: { id: string; name: string };
  employee: {
    id: string;
    personalNumber: string;
    position: string;
    name: string;
    showPosition: boolean;
  };
  eventTypeGroup: SubstituteEventTypeGroup[];
  manageableUnits?: {
    unit: {
      id: string;
      name: string;
    };
    allSetting?: {
      id: string;
      name: string;
    }[];
    directSetting?: {
      id: string;
      name: string;
    }[];
    roleInUnit: {
      label: string;
      role: 'manager' | 'assistant';
    };
    neverSetting?: {
      id: string;
      name: string;
    }[];
  }[];
}

export interface UserState {
  status: 'idle' | 'loading' | 'complete' | 'failed';
  data: ExtendedUser | null;
  error: AppError | null;
  candidateUser: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
    entity: CandidateItem | null;
  };
  subscriptions: {
    telegram:
      | 'idle'
      | 'enablingInProgress'
      | 'disablingInProgress'
      | 'enabled'
      | 'disabled';
  };
  processes: {
    certificateCreationInit: {
      inProgress: boolean;
      error: AppError | null;
    };
    certificateCreationVerify: {
      inProgress: boolean;
      error: AppError | null;
    };
  };
  token1C: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    expiresStatus: 'idle' | 'not_issued' | 'active' | 'expired';
    expiresAt: string | null;
    value: string | null;
    error: AppError | null;
  };
  permissions: Record<
    'company' | 'employee' | 'general',
    Required<UserPermissions>
  >;
  currentRequestId: string | null;
  eventTypes: Record<
    string,
    {
      status: 'idle' | 'loading' | 'complete' | 'failed';
      types: EventType[];
      error: AppError | null;
    }
  >;
  companyEventTypes: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: CompanyEventTypeItem[];
    error: AppError | null;
  };

  substitutes: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: CompanyPositionSubstitutes[];
    error: AppError | null;
  };

  unit: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
    entity: Unit | null;
  };

  showNotificationsBlock: boolean;

  profile: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
    employees: UserProfileEmployee[];
  };
}

const permissions = {
  view_events: false,
  view_event_to_paper: false,
  view_employees: false,
  view_policies: false,
  view_company: false,
  view_company_profiles: false,
  view_settings: false,
  view_attorneys: false,
  view_candidates: false,
  view_vacations: false,
  view_vacations_settings: false,
  view_payslips: false,
  view_smev_errors: false,
  view_absences: false,
  view_personal_profile: false,
  view_competencies: false,
  view_goal_management: false,
  view_units: false,
  view_social_news: false,
  view_social_categories: false,
  view_news: false,
  view_social_surveys: false,
  view_social_access: false,
  view_social_communities: false,
  view_social_feed: false,
  view_event_types_settings: false,
  view_templates_settings: false,
  view_groups_settings: false,
  view_substitutes_settings: false,
  view_sign_types: false,
  view_account_settings: false,
  view_account_dashboard_settings: false,
  view_quick_search: false,
  view_ph_side_menu: false,
  view_account_access_settings: false,
  view_account_general_settings: false,
  view_main_widgets_settings: false,
  view_social_widget_content: false,
  view_account_role_model_settings: false,
  view_business_trips: false,
  view_process_catalog: false,
  view_social_target_audience: false,
  view_social_favorites: false,
};

export const initialState: UserState = {
  status: 'idle',
  data: null,
  error: null,
  candidateUser: {
    status: 'idle',
    error: null,
    entity: null,
  },
  subscriptions: {
    telegram: 'idle',
  },
  processes: {
    certificateCreationInit: {
      inProgress: false,
      error: null,
    },
    certificateCreationVerify: {
      inProgress: false,
      error: null,
    },
  },
  token1C: {
    status: 'idle',
    expiresStatus: 'idle',
    expiresAt: null,
    value: null,
    error: null,
  },
  permissions: {
    employee: permissions,
    company: permissions,
    general: permissions,
  },
  currentRequestId: null,
  eventTypes: {},
  companyEventTypes: {
    status: 'idle',
    items: [],
    error: null,
  },
  substitutes: {
    status: 'idle',
    items: [],
    error: null,
  },
  unit: {
    status: 'idle',
    error: null,
    entity: null,
  },
  showNotificationsBlock: false,
  profile: {
    error: null,
    status: 'idle',
    employees: [],
  },
};
